import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section, Heading, Spinner, EmptyState } from "../components/Bits";
import PageHero from "../components/PageHero";
import ImageUpload from "../components/ImageUpload";
import { Heart, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Community() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", ingredients: "", steps: "", image_url: "" });
  const [commentText, setCommentText] = useState({});
  const { user } = useAuth() || {};

  const load = () => {
    setLoading(true);
    api.get("/community/recipes").then((r) => setRecipes(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Bitte zuerst anmelden");
    try {
      await api.post("/community/recipes", form);
      toast.success("Rezept geteilt!");
      setOpenForm(false);
      setForm({ title: "", description: "", ingredients: "", steps: "", image_url: "" });
      load();
    } catch {
      toast.error("Konnte nicht speichern");
    }
  };

  const like = async (id) => {
    if (!user) return toast.error("Bitte zuerst anmelden");
    try {
      await api.post(`/community/recipes/${id}/like`);
      load();
    } catch {}
  };

  const comment = async (id) => {
    if (!user) return toast.error("Bitte zuerst anmelden");
    const text = (commentText[id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/community/recipes/${id}/comment`, { text });
      setCommentText({ ...commentText, [id]: "" });
      load();
    } catch {}
  };

  return (
    <Section>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <PageHero slug="community" variant="soft"
        defaultOverline="Community"
        defaultTitle="Teile dein Wildkräuter-Rezept"
        defaultSubtitle="Bärlauchpesto, Brennnesselsuppe, Holundersirup – inspiriere die Community."
      />
        <button onClick={() => setOpenForm(!openForm)} className="btn-primary" data-testid="open-recipe-form">
          + Rezept teilen
        </button>
      </div>

      {openForm && (
        <form onSubmit={create} className="plant-card p-6 mb-10 space-y-4" data-testid="recipe-form">
          <input required placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-3 rounded-xl border" style={{ borderColor: "var(--wp-border)" }} />
          <textarea required placeholder="Kurze Beschreibung" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-xl border min-h-[80px]" style={{ borderColor: "var(--wp-border)" }} />
          <textarea placeholder="Zutaten (pro Zeile eine)" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="w-full px-4 py-3 rounded-xl border min-h-[80px]" style={{ borderColor: "var(--wp-border)" }} />
          <textarea placeholder="Zubereitung" value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} className="w-full px-4 py-3 rounded-xl border min-h-[100px]" style={{ borderColor: "var(--wp-border)" }} />
          <div>
            <div className="overline mb-2">Foto</div>
            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="recipe-image" />
          </div>
          <button className="btn-primary" data-testid="submit-recipe">Veröffentlichen</button>
        </form>
      )}

      {loading ? <Spinner /> : recipes.length === 0 ? (
        <EmptyState title="Noch keine Rezepte" description="Sei die/der Erste und teile dein Lieblings-Wildkräuter-Rezept!">
          {!user && <Link to="/login" className="btn-primary mt-2 inline-flex">Jetzt anmelden</Link>}
        </EmptyState>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="community-grid">
          {recipes.map((r) => {
            const liked = user && (r.likes || []).includes(user.id);
            return (
              <div key={r.id} className="plant-card overflow-hidden flex flex-col" data-testid={`community-recipe-${r.id}`}>
                {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-44 object-cover" />}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display text-xl mb-1">{r.title}</h3>
                  <div className="text-xs opacity-60 mb-2">von {r.user_name}</div>
                  <p className="text-sm opacity-80 flex-1">{r.description}</p>
                  {r.ingredients && <div className="mt-3 text-xs whitespace-pre-line opacity-80">{r.ingredients}</div>}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: "var(--wp-border)" }}>
                    <button onClick={() => like(r.id)} className={`flex items-center gap-1 text-sm ${liked ? "text-[var(--wp-toxic)]" : ""}`} data-testid={`like-${r.id}`}>
                      <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {(r.likes || []).length}
                    </button>
                    <div className="flex items-center gap-1 text-sm opacity-70">
                      <MessageCircle className="w-4 h-4" /> {(r.comments || []).length}
                    </div>
                  </div>
                  {(r.comments || []).slice(0, 3).map((c) => (
                    <div key={c.id} className="mt-2 text-xs"><b>{c.user_name}:</b> {c.text}</div>
                  ))}
                  {user && (
                    <div className="mt-3 flex gap-2">
                      <input value={commentText[r.id] || ""} onChange={(e) => setCommentText({ ...commentText, [r.id]: e.target.value })} placeholder="Kommentar…" className="flex-1 px-3 py-2 text-xs rounded-full border" style={{ borderColor: "var(--wp-border)" }} />
                      <button onClick={() => comment(r.id)} className="text-xs btn-secondary !py-1 !px-3">Senden</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
